
import React, { useState, useRef } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Camera, Mail, Bell } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile, useUpdateProfile, useUploadAvatar, AVAILABLE_LANGUAGES } from "@/services/profileService";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { useToast } from "@/hooks/use-toast";

const profileFormSchema = z.object({
  full_name: z.string().optional(),
  username: z.string().optional(),
  farm_name: z.string().optional(),
  farm_location: z.string().optional(),
  farm_size: z.number().optional().or(z.string().transform(val => val === "" ? undefined : Number(val))),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

const ProfilePage = () => {
  const { user } = useAuth();
  const { data: profile, isLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const { toast } = useToast();
  const { currentLanguage, setLanguage, availableLanguages } = useLanguage();
  const { pushEnabled, emailEnabled, togglePushNotifications, toggleEmailNotifications } = useNotifications();
  
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form initialization
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      full_name: profile?.full_name || "",
      username: profile?.username || "",
      farm_name: profile?.farm_name || "",
      farm_location: profile?.farm_location || "",
      farm_size: profile?.farm_size || undefined,
    },
    values: {
      full_name: profile?.full_name || "",
      username: profile?.username || "",
      farm_name: profile?.farm_name || "",
      farm_location: profile?.farm_location || "",
      farm_size: profile?.farm_size || undefined,
    }
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      setAvatarFile(file);
      const previewUrl = URL.createObjectURL(file);
      setAvatarPreview(previewUrl);
    }
  };

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSaveAvatar = async () => {
    if (avatarFile) {
      try {
        await uploadAvatar.mutateAsync(avatarFile);
        setAvatarFile(null);
        // Preview URL will be updated automatically when profile is refreshed
      } catch (error) {
        console.error("Erreur lors du téléchargement de l'avatar:", error);
      }
    }
  };

  const onSubmit = async (data: ProfileFormValues) => {
    if (!profile) return;
    
    try {
      await updateProfile.mutateAsync({
        ...data,
        id: profile.id
      });
    } catch (error) {
      console.error("Erreur lors de la mise à jour du profil:", error);
    }
  };

  if (isLoading) {
    return (
      <AppLayout title="Profil" showBack>
        <div className="text-center py-10">
          <p>Chargement du profil...</p>
        </div>
      </AppLayout>
    );
  }

  const getInitials = () => {
    if (profile?.full_name) {
      return profile.full_name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  return (
    <AppLayout title="Profil" showBack>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Votre Profil</CardTitle>
            <CardDescription>
              Gérez vos informations personnelles et vos paramètres de compte
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar section */}
            <div className="flex flex-col items-center space-y-4">
              <Avatar className="h-24 w-24 cursor-pointer" onClick={handleAvatarClick}>
                <AvatarImage src={avatarPreview || profile?.avatar_url || undefined} />
                <AvatarFallback className="text-2xl">{getInitials()}</AvatarFallback>
              </Avatar>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleAvatarClick}
                >
                  <Camera className="mr-2 h-4 w-4" />
                  Changer
                </Button>
                {avatarFile && (
                  <Button 
                    size="sm" 
                    onClick={handleSaveAvatar}
                    disabled={uploadAvatar.isPending}
                  >
                    Enregistrer
                  </Button>
                )}
              </div>
            </div>

            <Separator />

            {/* Profile form */}
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="full_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom complet</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom d'utilisateur</FormLabel>
                      <FormControl>
                        <Input placeholder="johndoe" {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="farm_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom de la ferme</FormLabel>
                      <FormControl>
                        <Input placeholder="Ma Ferme" {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="farm_location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Emplacement de la ferme</FormLabel>
                      <FormControl>
                        <Input placeholder="Paris, France" {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="farm_size"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Taille de la ferme (hectares)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="10" 
                          {...field}
                          value={field.value === undefined ? "" : field.value}
                          onChange={e => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={updateProfile.isPending}>
                  Sauvegarder les informations
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Préférences</CardTitle>
            <CardDescription>
              Gérez vos paramètres de langue et de notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Language section */}
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Langue</h3>
              <div className="max-w-xs">
                <Select 
                  value={currentLanguage} 
                  onValueChange={(value) => setLanguage(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une langue" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLanguages.map((language) => (
                      <SelectItem key={language.code} value={language.code}>
                        {language.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Notifications section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Notifications</h3>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <div className="flex items-center">
                    <Bell className="mr-2 h-4 w-4" />
                    <span className="text-sm font-medium">Notifications push</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Recevez des alertes directement dans votre navigateur
                  </p>
                </div>
                <Switch
                  checked={pushEnabled}
                  onCheckedChange={togglePushNotifications}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <div className="flex items-center">
                    <Mail className="mr-2 h-4 w-4" />
                    <span className="text-sm font-medium">Notifications par e-mail</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Recevez des mises à jour par e-mail sur vos activités
                  </p>
                </div>
                <Switch
                  checked={emailEnabled}
                  onCheckedChange={toggleEmailNotifications}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default ProfilePage;
